import { useCallback, useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { createdOrFailure } from '../lib/crm/storeGesture';
import { REOPEN_PATCH, stagePatch, type OpportunityPatch } from '../lib/crm/opportunitySheet';
import { useCRMStore } from '../store/crmStore';
import type { CRMOpportunity, CRMPipelineStage } from '../types/crm';

/**
 * The gestures a deal's sheet makes at once (PLAN-ENCRE-OUTILS O3): move it to a stage, close it
 * (won or lost) or reopen it, open its onboarding lead. Through the CRM store, so the pipeline's
 * kanban and figures follow; a refusal is said (`error`, a sentence), never taken for a success. After each
 * one the deal is read again (`reload`) — the database derives its probability and MRR.
 */
export function useOpportunityGestures(dealId: string | null, reload: () => Promise<void>, onLeadOpened: (leadId: string) => void) {
  const { updateOpportunity, createLeadFromOpportunity, calculateRevenue } = useCRMStore(
    useShallow((s) => ({
      updateOpportunity: s.updateOpportunity,
      createLeadFromOpportunity: s.createLeadFromOpportunity,
      calculateRevenue: s.calculateRevenue,
    })),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A refusal belongs to the deal it was about: another deal opens without it.
  useEffect(() => setError(null), [dealId]);

  const write = useCallback(
    async (patch: OpportunityPatch, withLead = false): Promise<boolean> => {
      if (!dealId) return false;
      setBusy(true);
      const refused = Object.keys(patch).length > 0 ? await updateOpportunity(dealId, patch as Partial<CRMOpportunity>) : null;
      let failed = refused ? `Pas enregistré : ${refused}` : null;
      let leadId: string | null = null;
      if (!failed && withLead) {
        const lead = createLeadFromOpportunity(dealId);
        const leadFailed = await createdOrFailure(lead, 'refus sans message');
        leadId = (await lead)?.id ?? null;
        // The deal's own change is saved: only the lead is said missing.
        if (leadFailed) failed = `Le lead d'onboarding n'a pas été créé : ${leadFailed}.${Object.keys(patch).length > 0 ? ' Le reste est enregistré.' : ''}`;
      }
      setBusy(false);
      setError(failed);
      if (!failed) void calculateRevenue();
      await reload();
      if (leadId) onLeadOpened(leadId);
      return !failed;
    },
    [dealId, updateOpportunity, createLeadFromOpportunity, calculateRevenue, reload, onLeadOpened],
  );

  return {
    busy,
    error,
    moveTo: (stage: CRMPipelineStage) => write(stagePatch(stage)),
    apply: write,
    reopen: () => write(REOPEN_PATCH),
    openLead: () => write({}, true),
  };
}
